import * as React from 'react';

import { DataTable } from '@/components/dataTable';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';

import { useDialogFormStore } from '@/components/dialog/form.store';
import { useAttributes } from '@/hooks/useAttributes';
import { useDynamicLocale } from '@/hoslog-intl';
import { formatDate } from '@/lib/dateFormatter';
import { exportCsvSingleFilePapa, filterCsvDataByVisibleColumns } from '@/lib/exportCsv';
import { formatAmount } from '@/lib/format';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import {
  CLUB_VERIFICATION_STATUS,
  ReservationLedger,
} from '@/services/dashboard/reservationLedger/reservationLedger.types';
import {
  getClubVerificationStatusLabel,
  getPaymentMethodLabel,
  getPaymentStatusLabel,
  getPlanTypeLabel,
} from '../utils/statusLabels';
import { TableActionBar } from './ActionBar';
import { getColumns } from './Column';

export function ReservationLedgerTable({
  data,
  isLoading = false,
  pagination,
}: {
  data: ReservationLedger[];
  isLoading: boolean;
  pagination: Pagination;
}) {
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const { onOpen } = useDialogFormStore();

  const { t, locale } = useDynamicLocale();
  const { data: reservationPlanTypes } = useAttributes('reservationPlanType');
  const columns = React.useMemo(
    () => getColumns(onOpen, t, reservationPlanTypes ?? [], locale),
    [onOpen, t, reservationPlanTypes, locale],
  );

  const EXPORT_KEYS_BY_COLUMN: Record<string, readonly string[]> = {
    sn: ['SN'],
    invoiceNumber: [t('common.page.tableColumn.resId')],
    nickName: [t('common.page.tableColumn.nickName')],
    storeInfo: [t('common.page.tableColumn.storeInfo')],
    createdDate: [t('common.page.tableColumn.reservartionDate')],
    reservationType: [t('common.page.tableColumn.reservationType')],
    planType: [t('common.page.tableColumn.planType')],
    planName: [t('common.page.tableColumn.planName')],
    numberOfPeople: [t('common.page.tableColumn.numberOfPeople')],
    clubVerificationStatus: [t('common.page.tableColumn.clubVerificationStatus')],
    paymentMethod: [t('common.page.tableColumn.paymentMethod')],
    paymentStatus: [t('common.page.tableColumn.paymentStatus')],
    totalPlanAmount: [t('common.page.tableColumn.totalPlanAmount')],
    totalPlanCommissionAmount: [t('common.page.tableColumn.totalPlanCommissionAmount')],
    totalClubAmount: [t('common.page.tableColumn.totalClubAmount')],
    totalAdminCommissionAmount: [t('common.page.tableColumn.totalAdminCommissionAmount')],
    totalAdminAmount: [t('common.page.tableColumn.totalAdminAmount')],
  };

  function handleExport() {
    const exportData = table.getFilteredRowModel().rows.map(({ original: row, index }) => {
      const visitingDateTime = row.visitingDateTime;
      const endingDateTime = row.endingDateTime;
      const date = formatDate(visitingDateTime, 'YYYY/MM/DD HH:mm').split(' ')[0];
      const visitingTime = formatDate(visitingDateTime, 'YYYY/MM/DD HH:mm').split(' ')[1];
      const endingTime = formatDate(endingDateTime, 'YYYY/MM/DD HH:mm').split(' ')[1];

      return {
        SN: getSerialNumber(table, index),
        [t('common.page.tableColumn.resId')]: row.invoiceNumber,
        [t('common.page.tableColumn.nickName')]: row.nickName,
        [t('common.page.tableColumn.storeInfo')]: [row.storeInfoEng, row.storeInfoJpn].filter(Boolean).join(', '),
        [t('common.page.tableColumn.reservartionDate')]: `${date} ${visitingTime}~${endingTime}`,
        [t('common.page.tableColumn.reservationType')]: row.reservationType,
        [t('common.page.tableColumn.planType')]: getPlanTypeLabel(row.planType, reservationPlanTypes ?? [], locale),
        [t('common.page.tableColumn.planName')]: row.planName,
        [t('common.page.tableColumn.numberOfPeople')]: row.numberOfPeople,
        [t('common.page.tableColumn.clubVerificationStatus')]: getClubVerificationStatusLabel(
          row.clubVerificationStatus,
          t,
        ),
        [t('common.page.tableColumn.paymentMethod')]: getPaymentMethodLabel(row.paymentMethod, t),
        [t('common.page.tableColumn.paymentStatus')]: getPaymentStatusLabel(row.paymentStatus, t),
        [t('common.page.tableColumn.totalPlanAmount')]: formatAmount(row.totalPlanAmount),
        [t('common.page.tableColumn.totalPlanCommissionAmount')]: formatAmount(row.totalPlanCommissionAmount),
        [t('common.page.tableColumn.totalClubAmount')]: formatAmount(row.totalClubAmount),
        [t('common.page.tableColumn.totalAdminCommissionAmount')]: formatAmount(row.totalAdminCommissionAmount),
        [t('common.page.tableColumn.totalAdminAmount')]: formatAmount(row.totalAdminAmount),
      };
    });

    const visibleColumnIds = table.getVisibleLeafColumns().map((column) => column.id);
    const visibleData = filterCsvDataByVisibleColumns(exportData, visibleColumnIds, EXPORT_KEYS_BY_COLUMN);

    exportCsvSingleFilePapa(visibleData, {
      format: 'YYYY/MM/DD HH:mm:ss',
    });
  }

  const { table, statusFilter, planFilter, onStatusFilterChange, onPlanFilterChange } = useDataTable({
    data,
    columns,
    pageCount,
    initialState: {
      sorting: [{ id: 'reservationId', desc: true }],
    },
    meta: {
      defaultDatePreset: 'today',
      title: t('dashboard.crumb.reservationLedger'),
      totalPage: pagination.totalItems,
      actionKey: 'exportCsv',
      action: handleExport,
      loading: isLoading,
    },
    getRowId: (originalRow) => String(originalRow.reservationId),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table}>
        <DataTableToolbar
          defaultDatePreset="today"
          allowFutureDates
          statusFilter={statusFilter as CLUB_VERIFICATION_STATUS}
          onStatusFilterChange={onStatusFilterChange}
          planFilter={planFilter as string}
          onPlanFilterChange={onPlanFilterChange}
        />
        <TableActionBar />
      </DataTable>
    </>
  );
}
